import { useEffect, useRef, useState } from "react"

import { ResultList } from "@/components/ResultCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useApi } from "@/lib/hooks"
import type { SearchResponse, SearchResult, SearchStreamEvent, SearchType } from "@shared/types"

const STARGAZER = String.raw`   *       .       +        .       *       .        *    +
       .       *       .        +       .       *       .
  +       .       *       .         +       .      .    *
     .      o/      *        .         +       .     +
           /|
           / \
_____ ___________ ____ _____________ ______ ________ ______`

function EmptyState() {
  return (
    <div className="space-y-3 py-12 text-muted-foreground">
      <pre className="overflow-x-auto text-base leading-snug">{STARGAZER}</pre>
      <p className="text-sm">Search, and you shall find.</p>
    </div>
  )
}

function useAgenticSearch() {
  const [query, setQuery] = useState("")
  const [steps, setSteps] = useState<string[]>([])
  const [results, setResults] = useState<SearchResult[]>([])
  const [err, setErr] = useState<string>()
  const [running, setRunning] = useState(false)
  const esRef = useRef<EventSource | null>(null)

  const reset = () => {
    esRef.current?.close()
    esRef.current = null
    setQuery("")
    setSteps([])
    setResults([])
    setErr(undefined)
    setRunning(false)
  }

  const start = (q: string) => {
    reset()
    setQuery(q)
    setRunning(true)
    const es = new EventSource(`/api/search?q=${encodeURIComponent(q)}&type=agentic`)
    esRef.current = es
    const finish = () => {
      setRunning(false)
      es.close()
    }
    es.onmessage = (e) => {
      let ev: SearchStreamEvent
      try {
        ev = JSON.parse(e.data) as SearchStreamEvent
      } catch {
        return
      }
      if (ev.type === "step") setSteps((s) => [...s, ev.text])
      else if (ev.type === "results") setResults(ev.results)
      else if (ev.type === "error") {
        setErr(ev.text)
        finish()
      } else if (ev.type === "done") finish()
    }
    es.onerror = finish
  }

  useEffect(() => () => esRef.current?.close(), [])

  return { query, steps, results, err, running, start, reset }
}

function AgenticResults({
  query,
  steps,
  results,
  err,
  running,
}: ReturnType<typeof useAgenticSearch>) {
  if (!query) return <EmptyState />
  return (
    <div className="space-y-4">
      {(steps.length > 0 || running) && (
        <ol className="space-y-1 rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
          {running && <li className="animate-pulse text-foreground">Thinking…</li>}
        </ol>
      )}

      {err && <p className="text-sm text-red-600">{err}</p>}

      {results.length > 0 && <ResultList results={results} query={query} />}
    </div>
  )
}

const MODES: SearchType[] = ["agentic", "semantic", "full-text"]

export function SearchTab() {
  const [textValue, setTextValue] = useState("")
  const [query, setQuery] = useState("")
  const [mode, setMode] = useState<SearchType>("agentic")

  const agentic = mode === "agentic"

  useEffect(() => {
    const id = setTimeout(() => setQuery(textValue.trim()), 300)
    return () => clearTimeout(id)
  }, [textValue])

  const url = query ? `/api/search?q=${encodeURIComponent(query)}&type=${mode}` : ""
  const { data, err } = useApi<SearchResponse>(agentic || !url ? null : () => fetch(url), [agentic, url])
  const agent = useAgenticSearch()

  const runAgentic = () => {
    const q = textValue.trim()
    if (q && !agent.running) agent.start(q)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 pt-1">
        {MODES.map((m) => (
          <label key={m} className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name="search-mode"
              checked={mode === m}
              onChange={() => {
                setMode(m)
                agent.reset() // don't carry a run across modes
              }}
            />
            {m}
          </label>
        ))}
      </div>

      <div className="flex gap-2">
        <Input
          autoFocus
          value={textValue}
          placeholder={agentic ? "Ask a question…" : "Search opinions and case names…"}
          onChange={(e) => setTextValue(e.target.value)}
          onKeyDown={(e) => {
            if (agentic && e.key === "Enter") runAgentic()
          }}
        />
        {agentic && (
          <Button className="min-w-[7rem]" onClick={runAgentic} disabled={agent.running || !textValue.trim()}>
            Search
          </Button>
        )}
      </div>

      {agentic ? (
        <AgenticResults {...agent} />
      ) : !query ? (
        <EmptyState />
      ) : err ? (
        <p className="py-4 text-sm text-red-600">{err}</p>
      ) : !data ? (
        <p className="py-4 text-sm text-muted-foreground">Loading…</p>
      ) : data.results.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">No matching cases.</p>
      ) : (
        <ResultList results={data.results} query={data.query} semantic={data.type === "semantic"} />
      )}
    </div>
  )
}
